/**
 * MakeOfferModal — buyer-side dialog opened from the Cart page for a single
 * seller-group. Captures amount + optional note and POSTs /api/offers.
 *
 * If the group contains any FIRM listings (accepts_offers=false), the modal
 * refuses to open and shows an inline error listing the offending items so
 * the buyer can remove them or ask the seller to unmark them.
 */
import { useState, useMemo } from "react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function MakeOfferModal({
  open,
  onOpenChange,
  group,             // { seller_id, seller_username, listings, subtotal, combined_shipping }
  paymentMethod,     // must match one the seller accepts
  onSubmitted,       // (offer) => void — called after successful POST
}) {
  const firmItems = useMemo(
    () => (group?.listings || []).filter((l) => !l.accepts_offers),
    [group],
  );
  const listedSubtotal = group?.subtotal || 0;
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);

  const canSubmit =
    !busy &&
    firmItems.length === 0 &&
    !!paymentMethod &&
    acceptTerms &&
    Number(amount) > 0 &&
    Number(amount) < listedSubtotal;

  async function submit() {
    if (!canSubmit) return;
    setBusy(true);
    try {
      const { data } = await api.post("/offers", {
        listing_ids: group.listings.map((l) => l.id),
        amount: Number(amount),
        note: note.trim() || null,
        payment_method: paymentMethod,
        accept_transaction_terms: true,
      });
      toast.success("Offer sent — waiting on seller.");
      onSubmitted?.(data.offer);
      // Reset form for next open.
      setAmount(""); setNote(""); setAcceptTerms(false);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent
        data-testid="make-offer-modal"
        className="!bg-[#121212] border border-white/15 text-zinc-200 shadow-2xl"
      >
        <AlertDialogHeader>
          <AlertDialogTitle className="font-display uppercase tracking-tight">
            Make an Offer to @{group?.seller_username}
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3 text-sm text-zinc-300">
              {firmItems.length > 0 ? (
                <div
                  className="border border-red-500/40 bg-red-500/10 p-3 text-[13px] text-red-200"
                  data-testid="make-offer-firm-error"
                >
                  <div className="font-display font-bold uppercase text-red-300 text-xs mb-1">
                    Offer includes Firm cards
                  </div>
                  <p>
                    Your cart from @{group.seller_username} contains{" "}
                    {firmItems.length === 1 ? "a card" : `${firmItems.length} cards`} marked as{" "}
                    <span className="font-semibold">Firm</span>. Please remove the Firm{" "}
                    {firmItems.length === 1 ? "card" : "cards"} from the group or ask the seller
                    to unmark them to proceed.
                  </p>
                  <ul className="mt-2 list-disc pl-5 space-y-0.5 text-red-100">
                    {firmItems.map((l) => (
                      <li key={l.id} className="text-[12px]">{l.title}</li>
                    ))}
                  </ul>
                </div>
              ) : (
                <>
                  <p>
                    You&apos;re bidding on{" "}
                    <span className="text-white font-semibold">
                      {group?.listings?.length} card{group?.listings?.length === 1 ? "" : "s"}
                    </span>{" "}
                    · listed subtotal:{" "}
                    <span className="text-white font-semibold">${listedSubtotal.toFixed(2)}</span>.
                    Enter what you&apos;d pay for the whole group. Shipping is negotiated by the
                    seller separately when they validate the PO.
                  </p>
                  <div>
                    <label className="h57-label">Your Offer (USD)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0.01"
                      max={listedSubtotal - 0.01}
                      className="h57-input"
                      data-testid="make-offer-amount"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      placeholder={`Below $${listedSubtotal.toFixed(2)}`}
                      autoFocus
                    />
                    {amount !== "" && Number(amount) >= listedSubtotal && (
                      <div className="text-[11px] text-red-400 mt-1">
                        Offer must be below the listed subtotal (${listedSubtotal.toFixed(2)}).
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="h57-label">Note to seller <span className="normal-case tracking-normal text-[10px] text-zinc-500">(optional)</span></label>
                    <textarea
                      className="h57-input min-h-[70px]"
                      data-testid="make-offer-note"
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      placeholder="e.g. Fan of the set — hope we can meet in the middle."
                      maxLength={500}
                    />
                  </div>
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      className="mt-1"
                      data-testid="make-offer-accept-terms"
                      checked={acceptTerms}
                      onChange={(e) => setAcceptTerms(e.target.checked)}
                    />
                    <span className="text-[11px] text-zinc-400">
                      If accepted, a Purchase Order opens automatically at the agreed amount and
                      I&apos;ll pay via <span className="text-white uppercase">{paymentMethod}</span>.
                      Hunt57 is not a party to any sale.
                    </span>
                  </label>
                </>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="make-offer-cancel">Cancel</AlertDialogCancel>
          {firmItems.length === 0 && (
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); submit(); }}
              disabled={!canSubmit}
              data-testid="make-offer-submit"
              className="disabled:opacity-40"
            >
              {busy ? "Sending…" : "Send Offer →"}
            </AlertDialogAction>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
